/**
 * StockPlaceLabel - Exibe o local de um item (endereço, prestador ou veículo do cliente)
 *
 * RELEASE: 2026-10-09 v1.3.0 - Prop opcional `suffix` exibida na segunda linha (ex.: "Junto com o kit").
 * RELEASE: 2026-10-09 v1.2.0 - Permite apresentação compacta na tabela de movimentações.
 * RELEASE: 2026-10-08 v1.1.0 - Exibe o tipo do local na segunda linha.
 * RELEASE: 2026-10-06 v1.0.0 - Versão inicial (Fase 4 do Estoque).
 */
import { Car, MapPin, Truck, Wrench } from 'lucide-react';
import type { StockPlace } from '@/hooks/useStockMovements';
import { stockPlaceTypeLabel } from '@/lib/stock-presentation';

const ICONS = { location: MapPin, provider: Wrench, customer: Car, carrier: Truck };

export function StockPlaceLabel({ place, showSince = false, compact = false, suffix }: {
  place: StockPlace | null | undefined;
  showSince?: boolean;
  compact?: boolean;
  /** Texto complementar na segunda linha, evitando uma terceira linha na tabela. */
  suffix?: string;
}) {
  if (!place) return <span className={compact ? 'text-[10px] text-muted-foreground' : 'text-xs text-muted-foreground'}>Sem local</span>;
  const Icon = ICONS[place.type] ?? MapPin;
  return (
    <div className={compact ? 'flex min-w-0 items-start gap-1.5 text-xs' : 'flex min-w-0 items-start gap-1.5 text-sm'}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <div className="truncate">{place.label ?? '—'}</div>
        <div className={compact ? 'truncate text-[10px] text-muted-foreground' : 'truncate text-xs text-muted-foreground'}>
          {stockPlaceTypeLabel(place.type)}
          {place.customer_name && ` · ${place.customer_name}`}
          {suffix && ` · ${suffix}`}
          {showSince && (
            <>
              {(place.customer_name || place.type) && ' · '}
            {showSince && `desde ${new Date(place.since).toLocaleDateString('pt-BR')}`}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
